import React, { FunctionComponent, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ImageSourcePropType, useWindowDimensions, View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import {
  LyraSelectComingSoonHeaderDesktop,
  LyraSelectComingSoonHeaderDesktopPlus,
  LyraSelectComingSoonHeaderLaptop,
  LyraSelectComingSoonHeaderMobile,
  LyraSelectComingSoonHeaderMobilePlus,
  LyraSelectComingSoonHeaderTablet,
  LyraSelectMicrositeHeaderDesktop,
  LyraSelectMicrositeHeaderDesktopPlus,
  LyraSelectMicrositeHeaderLaptop,
  LyraSelectMicrositeHeaderMobile,
  LyraSelectMicrositeHeaderMobilePlus,
  LyraSelectMicrositeHeaderTablet,
} from '../../../assets/microsite'
import { BodyText, Headline, PrimaryButton, Subhead } from '../../../atoms'
import { breakpoints as breakpointsConstant } from '../../../constants'
import { BodyTextSize, HeadlineSize, SubheadSize } from '../../../styles'
import { ThemeType, tID } from '../../../utils'
import { MicrositeExploreOnYourOwnCTA } from '../micrositeExploreOnYourOwnCTA/MicrositeExploreOnYourOwnCTA'

export interface LyraSelectHomepageHeroSectionProps {
  onExploreOnYourOwnCTAPress?: () => void
  onGetStartedPress?: () => void
  isComingSoon?: boolean
  onComingSoonPress?: () => void
  startDate?: string
}

const LyraSelectHeroContainer = styled.View<{ height: string; minHeight: string }>(({ height, minHeight }) => ({
  width: '100%',
  height: height,
  minHeight: minHeight,
}))

const TitleContainer = styled.View<{ padding: string; margin: string }>(({ padding, margin }) => ({
  padding: padding,
  margin: margin,
}))

const HeaderAndDescriptionTextContainer = styled.View<{ maxWidth: string }>(({ maxWidth }) => ({
  maxWidth: maxWidth,
}))

const StaticBackgroundContainer = styled.ImageBackground({
  width: '100%',
  height: '100%',
  position: 'absolute',
})

const DescriptionContainer = styled.View<{ margin: string }>(({ margin }) => ({
  margin: margin,
}))

const LaunchDateContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : theme.spacing['16px'],
}))

const PrimaryCTAButton = styled(PrimaryButton)<{ margin: string; isFullWidth: boolean }>(({ margin, isFullWidth }) => ({
  margin: margin,
  minWidth: '234px',
  ...(isFullWidth && {
    width: '100%',
  }),
}))

const MainContainer = styled.View({
  height: '100%',
  justifyContent: 'space-between',
})

const MicrositeExploreContainer = styled.View<{ margin: string }>(({ margin }) => ({
  margin: margin,
}))

type HeroSectionStylingMetadata = {
  backgroundImage: ImageSourcePropType
  buttonMargin: string
  descriptionMargin: string
  descriptionSubheadSize: SubheadSize
  exploreMargin: string
  headerHeadlineSize: HeadlineSize
  headerSubheadSize: SubheadSize
  imageHeight: number
  imageWidth: number
  maxTextWidth: string
  minHeight: string
  padding: string
  titleMargin: string
}

export const LyraSelectHomepageHeroSection: FunctionComponent<LyraSelectHomepageHeroSectionProps> = ({
  onExploreOnYourOwnCTAPress,
  onGetStartedPress,
  isComingSoon,
  onComingSoonPress,
  startDate,
}) => {
  const { breakpoints, spacing } = useTheme() as ThemeType
  const { width } = useWindowDimensions()
  const isMobileSmallSize = width < breakpointsConstant.mobileS
  const { formatDate } = useIntl()

  const heroSectionStyling: HeroSectionStylingMetadata = {
    backgroundImage: isComingSoon ? LyraSelectComingSoonHeaderMobile : LyraSelectMicrositeHeaderMobile,
    buttonMargin: '0 auto',
    descriptionMargin: `${spacing['8px']} 0 ${spacing['16px']} 0`,
    descriptionSubheadSize: SubheadSize.XSMALL,
    exploreMargin: `0 0 ${spacing['24px']} 0`,
    headerHeadlineSize: HeadlineSize.SMALL,
    headerSubheadSize: SubheadSize.LARGE,
    imageHeight: 601,
    imageWidth: 375,
    maxTextWidth: '583px',
    minHeight: '650px',
    padding: `${spacing['32px']} ${spacing['16px']} 0 ${spacing['16px']}`,
    titleMargin: '0 auto',
    ...(breakpoints.isMinWidthMobileXs && {
      backgroundImage: isComingSoon ? LyraSelectComingSoonHeaderMobilePlus : LyraSelectMicrositeHeaderMobilePlus,
      buttonMargin: '0 auto',
      imageHeight: 602,
      imageWidth: 458,
      padding: `${spacing['32px']} ${spacing['16px']} 0 ${spacing['16px']}`,
      titleMargin: '0 auto',
      headerSubheadSize: SubheadSize.XLARGE,
    }),
    ...(breakpoints.isMinWidthMobileS && {
      backgroundImage: isComingSoon ? LyraSelectComingSoonHeaderTablet : LyraSelectMicrositeHeaderTablet,
      buttonMargin: '0 auto',
      descriptionSubheadSize: SubheadSize.SMALL,
      imageHeight: 892,
      imageWidth: 768,
      headerHeadlineSize: HeadlineSize.MEDIUM,
      headerSubheadSize: SubheadSize.XLARGE,
      maxTextWidth: '550px',
      padding: `13% 60px 0 60px`,
      titleMargin: '0 auto',
    }),
    ...(breakpoints.isMinWidthTablet && {
      backgroundImage: isComingSoon ? LyraSelectComingSoonHeaderLaptop : LyraSelectMicrositeHeaderLaptop,
      buttonMargin: '0',
      descriptionMargin: `${spacing['16px']} 0 ${spacing['24px']} 0`,
      descriptionSubheadSize: SubheadSize.SMALL,
      exploreMargin: '0 0 42px 0',
      headerHeadlineSize: HeadlineSize.SMALL,
      headerSubheadSize: SubheadSize.XLARGE,
      imageHeight: 827,
      imageWidth: 992,
      maxTextWidth: '550px',
      minHeight: '',
      padding: '20% 0 0 5%',
      titleMargin: '0',
      verticalPadding: 0,
    }),
    ...(breakpoints.isMinWidthLaptop && {
      backgroundImage: isComingSoon ? LyraSelectComingSoonHeaderDesktop : LyraSelectMicrositeHeaderDesktop,
      buttonMargin: '0',
      descriptionMargin: `${spacing['16px']} 0 ${spacing['24px']} 0`,
      descriptionSubheadSize: SubheadSize.MEDIUM,
      exploreMargin: '0 0 74px 0',
      headerHeadlineSize: HeadlineSize.SMALL,
      headerSubheadSize: SubheadSize.XLARGE,
      imageHeight: 806,
      imageWidth: 1440,
      maxTextWidth: '583px',
      minHeight: '',
      padding: '20% 0 0 8.5%',
      titleMargin: '0',
      verticalPadding: 0,
    }),
    ...(breakpoints.isMinWidthDesktopL && {
      backgroundImage: isComingSoon ? LyraSelectComingSoonHeaderDesktopPlus : LyraSelectMicrositeHeaderDesktopPlus,
      buttonMargin: '0',
      descriptionMargin: `${spacing['16px']} 0 40px 0`,
      descriptionSubheadSize: SubheadSize.LARGE,
      exploreMargin: '0 0 175px 0',
      headerHeadlineSize: HeadlineSize.LARGE,
      headerSubheadSize: SubheadSize.XLARGE,
      imageWidth: 1920,
      imageHeight: 986,
      maxTextWidth: '738px',
      minHeight: '',
      padding: `12% 0 0 ${width > 1545 ? '14%' : '7%'}`,
      titleMargin: '0',
      verticalPadding: 0,
    }),
  }

  const sectionHeight = useMemo(() => {
    const aspectRatio = heroSectionStyling.imageWidth / heroSectionStyling.imageHeight
    return Math.round(width * (1 / aspectRatio))
  }, [heroSectionStyling.imageHeight, heroSectionStyling.imageWidth, width])

  const headerTitleText = (
    <FormattedMessage
      defaultMessage='For life’s everyday challenges'
      description='Lyra Select hero section header title'
    />
  )

  const comingSoonHeaderButtonText = (
    <FormattedMessage defaultMessage='Notify me' description='Lyra Select hero section coming soon button' />
  )

  const heroHeaderButtonText = (
    <FormattedMessage defaultMessage='Get started' description='Lyra Select hero section get started button' />
  )

  const launchDateText = (
    <FormattedMessage
      defaultMessage='Launching {launchDate}'
      description='Header above the notify me on launch button'
      values={{
        launchDate: formatDate(startDate, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        }),
      }}
    />
  )

  const getHeaderCTAButton = (text: React.ReactNode, testId: string, onPressCallback?: () => void) => {
    return (
      <PrimaryCTAButton
        margin={heroSectionStyling.buttonMargin}
        text={text}
        onPress={() => {
          onPressCallback && onPressCallback()
        }}
        testID={tID(testId)}
        fullWidth={isMobileSmallSize}
        isFullWidth={isMobileSmallSize}
      />
    )
  }

  return (
    <LyraSelectHeroContainer
      height={`${sectionHeight}px`}
      testID={'LyraSelectHeroSection-heroContainer'}
      minHeight={heroSectionStyling.minHeight}
    >
      <StaticBackgroundContainer source={heroSectionStyling.backgroundImage} />
      <MainContainer>
        <TitleContainer padding={heroSectionStyling.padding} margin={heroSectionStyling.titleMargin}>
          <HeaderAndDescriptionTextContainer maxWidth={heroSectionStyling.maxTextWidth}>
            {isComingSoon && (
              <LaunchDateContainer>
                {breakpoints.isMobileSized ? (
                  <BodyText size={BodyTextSize.DEFAULT} text={launchDateText} textAlign={'center'} />
                ) : (
                  <Subhead size={heroSectionStyling.descriptionSubheadSize} text={launchDateText} textAlign={'left'} />
                )}
              </LaunchDateContainer>
            )}
            <View>
              {breakpoints.isMobileSized ? (
                <Subhead size={heroSectionStyling.headerSubheadSize} textAlign='center' text={headerTitleText} />
              ) : (
                <Headline size={heroSectionStyling.headerHeadlineSize} text={headerTitleText} />
              )}
            </View>
            <DescriptionContainer margin={heroSectionStyling.descriptionMargin}>
              <Subhead
                size={heroSectionStyling.descriptionSubheadSize}
                level='2'
                text={
                  <FormattedMessage
                    defaultMessage='Access personalized self-care, meditations, 1-on-1 care with a provider, and more.'
                    description='Lyra Select hero section header description'
                  />
                }
                textAlign={breakpoints.isMobileSized ? 'center' : 'left'}
              />
            </DescriptionContainer>
          </HeaderAndDescriptionTextContainer>
          {isComingSoon
            ? getHeaderCTAButton(
                comingSoonHeaderButtonText,
                'LyraSelectHeroSection-comingSoonButton',
                onComingSoonPress,
              )
            : getHeaderCTAButton(heroHeaderButtonText, 'LyraSelectHeroSection-getStartedButton', onGetStartedPress)}
        </TitleContainer>
        <MicrositeExploreContainer margin={heroSectionStyling.exploreMargin}>
          <MicrositeExploreOnYourOwnCTA
            onExploreOnYourOwnCTAPress={onExploreOnYourOwnCTAPress}
            testId={'LyraSelectHeroSection-exploreOnYourOwnCTA'}
          />
        </MicrositeExploreContainer>
      </MainContainer>
    </LyraSelectHeroContainer>
  )
}
